"use client";

import Img from '@/components/Img';
import { useCallback, useState } from "react";
import { InfoTooltip } from "@/components/ui/InfoTooltip";
import { useLocale } from "@/lib/locale-context";

// Витрина «Условия аренды» — 1-в-1 со страницей /conditions Солнышко
// (ConditionsPageClient): те же блоки/текст/места подачи/регионы/оборудование/
// услуги, акцент перекрашен в синий бренд Автопилота. Отличие: единый сайт —
// сверху КАРУСЕЛЬ ГОРОДОВ, при смене города клиентом перезапрашиваются места
// подачи/регионы/оборудование/услуги выбранного города (его station).
const ACCENT = "#0039A6";

function isFree(price: unknown): boolean {
  if (price == null || price === "") return true;
  const n = typeof price === "number" ? price : parseFloat(String(price));
  return !isFinite(n) || n <= 0;
}

type CondBlock = { icon: string; title: string; value: string; note?: string };
export interface CondConfig {
  blocks: CondBlock[];
  text: string;
  show_delivery: boolean;
  show_regions: boolean;
  show_equipment: boolean;
  show_services: boolean;
}
type CityOpt = { station: string; name: string; name_en?: string; currency?: string };
type CondData = { deliveryCities: any[]; deliveryRegions: any[]; services: any[]; equipment: any[] };

interface Props {
  h1: string;
  config: CondConfig;
  cities: CityOpt[];
  initialStation: string;
  initial: CondData;
}

export default function ConditionsPageClient({ h1, config, cities, initialStation, initial }: Props) {
  const { t, locale } = useLocale();
  const [station, setStation] = useState(initialStation);
  const [data, setData] = useState<CondData>(initial);
  const [loading, setLoading] = useState(false);
  const cur = cities.find((c) => c.station === station)?.currency || "₽";
  const free = t("search.free");

  const selectCity = useCallback(async (st: string) => {
    if (st === station || loading) return;
    setStation(st);
    setLoading(true);
    try {
      const [dRes, oRes] = await Promise.all([
        fetch(`/api/v1/delivery-places/${st}`, { cache: "no-store" }),
        fetch(`/api/v1/options/${st}`, { cache: "no-store" }),
      ]);
      const d = dRes.ok ? await dRes.json() : {};
      const o = oRes.ok ? await oRes.json() : {};
      setData({
        deliveryCities: (d.cities || []).filter((c: any) => !c.dont_see_on_site),
        deliveryRegions: (d.regions || []).filter((r: any) => !r.dont_see_on_site && r.name),
        services: (o.services || []).filter((s: any) => s.is_active),
        equipment: (o.equipment || []).filter((e: any) => e.is_active),
      });
    } catch {
      setData({ deliveryCities: [], deliveryRegions: [], services: [], equipment: [] });
    } finally {
      setLoading(false);
    }
  }, [station, loading]);

  const { deliveryCities, deliveryRegions, services, equipment } = data;

  return (
    <div style={{ maxWidth: 1000, margin: "0 auto", padding: "60px 20px 85px" }}>
      <h1 style={{ fontSize: 36, fontWeight: "bold", color: "#000", marginBottom: 0 }}>{h1}</h1>
      <div style={{ width: 80, height: 3, background: ACCENT, margin: "20px 0 28px" }} />

      {/* City carousel — переключение города меняет места подачи/регионы/опции */}
      {cities.length > 1 && (
        <div style={{ display: "flex", gap: 10, overflowX: "auto", paddingBottom: 12, marginBottom: 36, WebkitOverflowScrolling: "touch" }}>
          {cities.map((c) => {
            const active = c.station === station;
            return (
              <button
                key={c.station}
                type="button"
                onClick={() => selectCity(c.station)}
                style={{
                  flexShrink: 0, padding: "10px 20px", borderRadius: 24, cursor: "pointer",
                  fontSize: 15, fontWeight: active ? 700 : 500, fontFamily: "inherit",
                  border: active ? `1px solid ${ACCENT}` : "1px solid #E0E0E5",
                  background: active ? ACCENT : "#fff",
                  color: active ? "#fff" : "#1E1E2D",
                  transition: "all 0.15s ease",
                }}
              >
                {locale === "en" && (c as { name_en?: string }).name_en ? (c as { name_en?: string }).name_en : c.name}
              </button>
            );
          })}
        </div>
      )}

      {/* Info blocks */}
      {config.blocks.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 20, marginBottom: 40 }}>
          {config.blocks.map((block, i) => (
            <div key={i} style={{ background: "#F9F9FB", borderRadius: 12, padding: "24px 20px", display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 10 }}>
              {block.icon ? (
                <Img src={block.icon} alt="" style={{ width: 40, height: 40, objectFit: "contain" }} />
              ) : (
                <div style={{ width: 40, height: 40, borderRadius: "50%", background: ACCENT, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                </div>
              )}
              <div style={{ fontSize: 13, fontWeight: 600, color: "#888", textTransform: "uppercase", letterSpacing: "0.3px" }}>
                {block.title}
              </div>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#1E1E2D" }}>
                {block.value}
              </div>
              {block.note && (
                <div style={{ fontSize: 12, color: "#888", lineHeight: 1.45, marginTop: 4, whiteSpace: "pre-line" }}>
                  <span style={{ color: ACCENT, fontWeight: 700 }}>* </span>{block.note.replace(/^\s*\*+\s*/, "")}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Text content from config */}
      {config.text && (
        <>
          <style>{`
            .cond-text h2 { color: #000; font-size: 24px; font-weight: bold; margin: 32px 0 16px 0; }
            .cond-text p { color: #333; font-size: 16px; line-height: 1.7; margin: 0 0 14px 0; }
            .cond-text ul, .cond-text ol { color: #333; font-size: 16px; line-height: 1.8; margin: 0 0 14px 0; padding-left: 24px; }
            .cond-text ul { list-style-type: disc; }
            .cond-text li { margin-bottom: 6px; display: list-item; }
            .cond-text strong { font-weight: 700; }
          `}</style>
          <div className="cond-text" dangerouslySetInnerHTML={{ __html: config.text }} />
        </>
      )}

      {loading && (
        <div style={{ padding: "24px 0", color: "#8E8E93", fontSize: 15 }}>{t("cond.loadingCity")}</div>
      )}

      {/* Delivery places */}
      {config.show_delivery && deliveryCities.length > 0 && (
        <div style={{ marginTop: 48 }}>
          <h2 style={{ fontSize: 24, fontWeight: "bold", color: "#000", marginBottom: 20 }}>{t("cond.deliveryPlaces")}</h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
            {deliveryCities.map((city: any) => (
              <div key={city.guid || city.name} style={{ background: "#F9F9FB", borderRadius: 10, padding: "14px 18px" }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: "#1E1E2D", marginBottom: 6 }}>
                  {locale === "en" && city.name_en ? city.name_en : city.name}
                  <span style={{ color: ACCENT, marginLeft: 8, whiteSpace: "nowrap" }}>
                    {isFree(city.price) ? free : `${city.price} ${cur}`}
                  </span>
                </div>
                {city.addresses?.filter((a: any) => !a.dont_see_on_site).map((addr: any) => (
                  <div key={addr.guid || addr.name} style={{ fontSize: 13, color: "#666", paddingLeft: 12, marginBottom: 3 }}>
                    {locale === "en" && addr.name_en ? addr.name_en : addr.name}
                    <span style={{ color: isFree(addr.sum) ? ACCENT : "#999", marginLeft: 6, whiteSpace: "nowrap" }}>
                      {isFree(addr.sum) ? free : `${addr.sum} ${cur}`}
                    </span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Regions */}
      {config.show_regions && deliveryRegions.length > 0 && (
        <div style={{ marginTop: 40 }}>
          <h2 style={{ fontSize: 24, fontWeight: "bold", color: "#000", marginBottom: 20 }}>{t("cond.regions")}</h2>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "2px solid #EDEDF0" }}>
                <th style={{ padding: "10px 0", textAlign: "left", fontSize: 13, fontWeight: 600, color: "#888" }}>{t("cond.region")}</th>
                <th style={{ padding: "10px 0", textAlign: "right", fontSize: 13, fontWeight: 600, color: "#888", width: 120 }}>{t("cond.cost")}</th>
              </tr>
            </thead>
            <tbody>
              {deliveryRegions.map((r: any) => (
                <tr key={r.guid || r.name} style={{ borderBottom: "1px solid #F5F5F7" }}>
                  <td style={{ padding: "12px 0", fontSize: 15, color: "#333" }}>{locale === "en" && r.name_en ? r.name_en : r.name}</td>
                  <td style={{ padding: "12px 0", textAlign: "right", fontSize: 15, fontWeight: 600, color: ACCENT, whiteSpace: "nowrap" }}>
                    {isFree(r.price)
                      ? free
                      : `${r.is_from ? t("unit.from") : ""}${r.price} ${cur}${(r.daily_payment || r.is_day) ? ` ${t("unit.perDay")}` : ""}`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Equipment */}
      {config.show_equipment && equipment.length > 0 && (
        <div style={{ marginTop: 40 }}>
          <h2 style={{ fontSize: 24, fontWeight: "bold", color: "#000", marginBottom: 20 }}>{t("cond.equipment")}</h2>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <tbody>
              {equipment.map((e: any) => (
                <tr key={e.guid} style={{ borderBottom: "1px solid #F5F5F7" }}>
                  <td style={{ padding: "12px 0", fontSize: 15, color: "#333" }}>
                    <span style={{ display: "inline-flex", alignItems: "center", flexWrap: "wrap" }}>
                      <span>{locale === "en" && e.name_en ? e.name_en : e.name}</span>
                      <InfoTooltip html={(locale === "en" && e.description_en ? e.description_en : e.description_ru) || ""} title={locale === "en" && e.name_en ? e.name_en : e.name} />
                    </span>
                  </td>
                  <td style={{ padding: "12px 0", textAlign: "right", fontSize: 15, fontWeight: 600, color: isFree(e.price) ? ACCENT : "#1E1E2D", width: 140, whiteSpace: "nowrap" }}>{isFree(e.price) ? free : (<>{e.is_from ? t("unit.from") : ""}{e.price}&nbsp;{cur}</>)}</td>
                  <td style={{ padding: "12px 0", textAlign: "right", fontSize: 12, color: "#999", width: 80 }}>{e.is_day ? t("unit.perDay") : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Services */}
      {config.show_services && services.length > 0 && (
        <div style={{ marginTop: 40 }}>
          <h2 style={{ fontSize: 24, fontWeight: "bold", color: "#000", marginBottom: 20 }}>{t("cond.services")}</h2>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <tbody>
              {services.map((s: any) => (
                <tr key={s.guid} style={{ borderBottom: "1px solid #F5F5F7" }}>
                  <td style={{ padding: "12px 0", fontSize: 15, color: "#333" }}>
                    <span style={{ display: "inline-flex", alignItems: "center", flexWrap: "wrap" }}>
                      <span>{locale === "en" && s.name_en ? s.name_en : s.name}</span>
                      <InfoTooltip html={(locale === "en" && s.description_en ? s.description_en : s.description_ru) || ""} title={locale === "en" && s.name_en ? s.name_en : s.name} />
                    </span>
                  </td>
                  <td style={{ padding: "12px 0", textAlign: "right", fontSize: 15, fontWeight: 600, color: isFree(s.price) && (s.price_max == null || isFree(s.price_max)) ? ACCENT : "#1E1E2D", width: 150, whiteSpace: "nowrap" }}>
                    {isFree(s.price) && (s.price_max == null || isFree(s.price_max))
                      ? free
                      : (s.price_max > s.price
                        ? `${s.is_from ? t("unit.from") : ""}${s.price} — ${s.price_max} ${cur}`
                        : `${s.is_from ? t("unit.from") : ""}${s.price} ${cur}`)}
                  </td>
                  <td style={{ padding: "12px 0", textAlign: "right", fontSize: 12, color: "#999", width: 80 }}>{s.is_day ? t("unit.perDay") : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
